import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import colors from 'theme/_colors.scss';
import {
    IconAssetOffline, IconAssetOnline, IconBroadcast, IconEdit,
    IconTick,
    IconClose,
    IconAssetWarning,
    IconInfo,
    IconRefresh
} from 'svgIcons/MotionPortalIcons';
import useUserCapability from 'sharedHooks/useUserCapability';
import { ContextMenu } from 'sharedComponents/ContextMenu';
import useGatewayUserCapability from 'sharedHooks/useGatewayUserCapability';

import './style.scss';
import useHeaderMenus from './useHeaderMenus';
import { actionTypes, gatewayType, GATEWAY_STATUS_CLASS } from './constants';
import { GATWAY_CONNECTIVITY_STATUS, UNKNOWN_GATEWAY_TYPE } from '../constants';
import { lastCommandStatusActivateRemoteConnection } from './ActivateDeactivateGateway/actions';
import { requestType } from './ActivateDeactivateGateway/constants';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';

const HeaderComponent = (props) => {
    const { t: translate } = useTranslation();
    const dispatch = useDispatch();
    const { data } = useSelector(state => state.powertrainTools?.lastCommandStatus);
    const { gatewayInformation, gatewayStatus, gatewayID, currentFilters, currentSiteIds, onResetAssetInfo, gatewayFilter } = props;
    const { getGatewayInfo, getGatewayConnectedAssets } = props.actions || {};
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(gatewayInformation?.gatewayInfo?.gatewayName || '');
    const [showGatewayStatusInfo, setShowGatewayStatusInfo] = useState(false);
    const originalName = gatewayInformation?.gatewayInfo?.gatewayName || '';
    const isNameChanged =
        editedName.trim().length > 0 && editedName.trim() !== originalName.trim();
    const userCapability = useUserCapability();
    const userGatewayCapability = useGatewayUserCapability();
    const hasGatewayAdminViewCapability = userCapability?.hasGatewayAdminViewCapability?.() === true;
    const hasGatewayAdminReadViewCapability = userCapability?.hasGatewayAdminReadView?.() === true;
    const isGatewayReadOnlyMode = hasGatewayAdminReadViewCapability && !hasGatewayAdminViewCapability;
    const hasActivateRemoteConnectionCapability = userGatewayCapability?.hasActivateRemoteConnection();
    const isUnknownGateway = gatewayInformation?.gatewayInfo?.gatewaySerialNumber === UNKNOWN_GATEWAY_TYPE;

    useEffect(() => {
        if (hasActivateRemoteConnectionCapability && gatewayID) {
            dispatch(lastCommandStatusActivateRemoteConnection(gatewayID, requestType.ACTIVATED_REMOTE_CONNECTION));
        }
    }, [hasActivateRemoteConnectionCapability]);

    const [headerMenus, selectedHeaderMenu] = useHeaderMenus(gatewayID, gatewayInformation, data?.requestType);
    const canShowHeaderMenu = !isGatewayReadOnlyMode && !isEmpty(headerMenus);

    const toggleSelection = (selectedMenuItemId) => {
        dispatch({ type: actionTypes.SELECT_MENU_ITEM, selectedMenuItem: selectedMenuItemId });
        const menu = (headerMenus ?? []).find(m => m?.id === selectedMenuItemId);
        if (menu?.menuAction) {
            menu.menuAction(dispatch, gatewayID);
        }
    };
    const handleEditClick = () => {
        setEditedName(gatewayInformation?.gatewayInfo?.gatewayName || '');
        setIsEditing(true);
    };

    const handleSaveClick = () => {
        if (isNameChanged) {
            props.actions.renameGateway(gatewayID, editedName.trim(), props?.model?.selectedOrganization?.organizationID, currentFilters, currentSiteIds, gatewayFilter);
            setIsEditing(false);
        }
    };

    const handleCancelClick = () => {
        setIsEditing(false);
        setEditedName(gatewayInformation?.gatewayInfo?.gatewayName || '');
    };

    const showGatewayStatusInfoModal = () => {
        setShowGatewayStatusInfo(true);
    };

    const getGatewayStatusInfoTitle = (gatewayInformation) => {
        let title = '';
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.NETA21) {
            title = translate('ABB.Powertrain.Frontend.gatewayManagementConnectionInfoNETATitle');
        }
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.EGW02) {
            title = translate('ABB.Powertrain.Frontend.gatewayManagementConnectionInfoEGWTitle');
        }
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.DCP) {
            title = translate('ABB.Powertrain.Frontend.gatewayManagementConnectionInfoDCPTitle');
        }
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.CASSIA) {
            title = translate('ABB.Powertrain.Frontend.gatewayManagementConnectionInfoCassiaTitle');
        }
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.MACHSENSER) {
            title = translate('ABB.Powertrain.Frontend.gatewayManagementConnectionInfoMachineGuardianTitle');
        }
        return <>
            <IconInfo color={colors.black} width={16} height={16} />
            <span className='gateway-status-info-title-text ms-1'>{title}</span>
        </>
            ;
    };

    const renderGatewayStatusInfoContent = (gatewayInformation) => {
        let renderGatewayStatusInfo = null;
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.NETA21) {
            renderGatewayStatusInfo = <div className='gateway-info-content'>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementConnectionStatusDescription')}</p>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementNETAHeartbeatConnectionDescription')}</p>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementNETATelemetryConnectionDescription')}</p>
            </div>;
        }
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.EGW02) {
            renderGatewayStatusInfo = <div className='gateway-info-content'>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementConnectionStatusDescription')}</p>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementEGWBriefDisconnectionsDescription')}</p>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementEGWConnectionDescription')}</p>
            </div>;
        }
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.DCP) {
            renderGatewayStatusInfo = <div className='gateway-info-content'>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementConnectionStatusDescription')}</p>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementDCPDescription')}</p>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementDCPConnectionDescription')}</p>
            </div>;
        }
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.CASSIA) {
            renderGatewayStatusInfo = <div className='gateway-info-content'>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementConnectionStatusDescription')}{translate('ABB.Powertrain.Frontend.gatewayManagementCassiaHeadDescription')}</p>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementCassiaDescription')}</p>
            </div>;
        }
        if (gatewayInformation?.gatewayInfo?.gatewayType === gatewayType.MACHSENSER) {
            renderGatewayStatusInfo = <div className='gateway-info-content'>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementConnectionStatusDescription')}</p>
                <p>{translate('ABB.Powertrain.Frontend.gatewayManagementMachineGuardianDescription')}</p>
            </div>;
        }
        return renderGatewayStatusInfo;
    };

    const getStatusIndicatorClass = (status) => {
        if (status === GATWAY_CONNECTIVITY_STATUS.CONNECTED) { return GATEWAY_STATUS_CLASS.CONNECTED; }
        if (status === GATWAY_CONNECTIVITY_STATUS.DISCONNECTED) { return GATEWAY_STATUS_CLASS.DISCONNECTED; }
        if (status === GATWAY_CONNECTIVITY_STATUS.CONNECTED_WITH_WARNING) { return GATEWAY_STATUS_CLASS.CONNECTED_WITH_WARNING; }
        return '';
    };

    const renderStatusIcon = (status) => {
        if (status === GATWAY_CONNECTIVITY_STATUS.CONNECTED) { return <IconAssetOnline />; }
        if (status === GATWAY_CONNECTIVITY_STATUS.DISCONNECTED) { return <IconAssetOffline />; }
        if (status === GATWAY_CONNECTIVITY_STATUS.CONNECTED_WITH_WARNING) { return <IconAssetWarning />; }
        return null;
    };

    const getStatusText = (status) => {
        if (status === GATWAY_CONNECTIVITY_STATUS.CONNECTED) { return translate('ABB.Powertrain.Frontend.gatewayDetailsConnectedStatus'); }
        if (status === GATWAY_CONNECTIVITY_STATUS.DISCONNECTED) { return translate('ABB.Powertrain.Frontend.gatewayDetailsDisconnectedStatus'); }
        if (status === GATWAY_CONNECTIVITY_STATUS.CONNECTED_WITH_WARNING) { return translate('ABB.Powertrain.Frontend.gatewayDetailsConnectedWithWarningStatus'); }
        return null;
    };

    return (
        <>
            <div className='right-pane__header'>
                <div className='header__left-content'>
                    <IconBroadcast width={32} height={32} />
                    <span className='title'>
                        {isEditing ?
                            <>
                                <input
                                    type='text'
                                    value={editedName}
                                    onChange={e => setEditedName(e.target.value)}
                                    className='gateway-name-input'
                                    autoFocus
                                    maxLength={50}
                                />
                                <button type='button' className='icon-ok' data-testid='icon-ok' onClick={isNameChanged ? handleSaveClick : undefined}><IconTick /></button>
                                <button type='button' className='icon-close' data-testid='icon-close' onClick={handleCancelClick}><IconClose /></button>
                            </>
                            :
                            <>
                                {gatewayInformation?.gatewayInfo?.gatewayName || '-'}
                            </>
                        }
                    </span>
                    {!isGatewayReadOnlyMode && !isEditing && userCapability.hasAssetAdminCapability() && gatewayID &&
                        <button type='button' className='icon-edit' data-testid='icon-edit' onClick={handleEditClick}>
                            <IconEdit width={18} height={18} />
                        </button>
                    }
                </div >
                <div className='header__right-content'>
                    {!isUnknownGateway &&
                        <div className='header__gateway-status' id='header-gateway-status'>
                            <div className={`status-indicator ${getStatusIndicatorClass(gatewayStatus)}`}>
                                {renderStatusIcon(gatewayStatus)}
                            </div>
                            <span className='connected-cloud pe-2 d-inline-block' id='connected-status-text'>
                                {getStatusText(gatewayStatus)}
                            </span>
                            <button className='gateway-status-info-btn' id='gateway-status-info-btn' onClick={() => showGatewayStatusInfoModal()}>
                                <IconInfo color={colors.black} width={16} height={16} />
                            </button>
                            <button className='gateway-details-refresh-btn ms-3' id='gateway-details-refresh-btn' onClick={() => { getGatewayInfo(gatewayID); getGatewayConnectedAssets(gatewayID); }}>
                                <IconRefresh color={colors.black} width={16} height={16} />
                            </button>
                        </div>
                    }
                    {canShowHeaderMenu &&
                        <div className='header__context-menu' id='header-context-menu'>
                            <ContextMenu
                                options={headerMenus}
                                onSelect={toggleSelection}
                            />
                        </div>}
                </div>
            </div >
            {
                !isGatewayReadOnlyMode && selectedHeaderMenu?.modalContentComponent &&
                <selectedHeaderMenu.modalContentComponent
                    show={!!selectedHeaderMenu}
                    close={() => toggleSelection(null)}
                    title={selectedHeaderMenu.title}
                    gatewayID={gatewayID}
                    gatewaySerialNumber={gatewayInformation?.gatewayInfo?.gatewaySerialNumber}
                    gatewayType={gatewayInformation?.gatewayInfo?.gatewayType}
                    iotDeviceId={gatewayInformation?.gatewayInfo?.iotDeviceId}
                    organizationID={props?.model?.selectedOrganization?.organizationID}
                    onResetAssetInfo={onResetAssetInfo}
                    gatewayFilter={gatewayFilter}
                />
            }
            {
                <GeneralDialog
                    show={showGatewayStatusInfo}
                    close={() => setShowGatewayStatusInfo(false)}
                    closeButton={true}
                    title={getGatewayStatusInfoTitle(gatewayInformation)}
                    acceptButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.closeBtnLbl'),
                        onClick: () => {
                            setShowGatewayStatusInfo(false);
                        }
                    }}
                >
                    {renderGatewayStatusInfoContent(gatewayInformation)}
                </GeneralDialog>
            }
        </>
    );
};

HeaderComponent.propTypes = {
    gatewayInformation: PropTypes.shape({
        gatewayInfo: PropTypes.shape({
            gatewayName: PropTypes.string,
            gatewayType: PropTypes.string,
            gatewaySerialNumber: PropTypes.string,
            iotDeviceId: PropTypes.string,
        })
    }),
    actions: PropTypes.shape({
        getGatewayInfo: PropTypes.func,
        getGatewayConnectedAssets: PropTypes.func,
        renameGateway: PropTypes.func,
    }),
    gatewayStatus: PropTypes.string,
    gatewayID: PropTypes.number,
    statusChangeTimestamp: PropTypes.string,
    currentFilters: PropTypes.object,
    currentSiteIds: PropTypes.array,
    onResetAssetInfo: PropTypes.func,
    gatewayFilter: PropTypes.shape({
        type: PropTypes.string,
        subType: PropTypes.string
    })
};

export default HeaderComponent;
